/**
 * Agencies Page
 *
 * Aggregated view of real estate agencies derived from the deduplicated
 * property database. Each row represents a unique agency_name value with
 * summary statistics.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Building2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  ArrowUpDown,
  Home,
  MapPin,
} from "lucide-react";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Badge } from "@repo/ui/badge";
import { Card, CardContent } from "@repo/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { cn } from "@repo/ui/lib/utils";
import {
  fetchAgencies,
  getAgencyStats,
} from "@/lib/api/agencies";
import type { AgencySummary, AgencyFilters, AgencyStats } from "@/lib/api/agencies";
import { LISTING_TYPE_LABELS } from "@/lib/api/propertyDatabase";

const STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const PAGE_SIZE = 50;

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return "-";
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

type SortField = "property_count" | "agency_name" | "last_seen_at";

const Agencies = () => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState<AgencySummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<AgencyStats>({
    uniqueAgencies: 0,
    propertiesWithAgency: 0,
    totalProperties: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [sortBy, setSortBy] = useState<SortField>("property_count");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const filters = useMemo<AgencyFilters>(
    () => ({
      search: searchQuery || undefined,
      state: stateFilter !== "all" ? stateFilter : undefined,
    }),
    [searchQuery, stateFilter],
  );
  const filtersResetKey = `${searchQuery}|${stateFilter}`;

  const loadAgencies = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const offset = (currentPage - 1) * PAGE_SIZE;

      const [result, statsResult] = await Promise.all([
        fetchAgencies({ filters, limit: PAGE_SIZE, offset, sortBy, sortOrder }),
        getAgencyStats(),
      ]);

      setAgencies(result.data);
      setTotalCount(result.total);
      setStats(statsResult);
    } catch (error) {
      console.error("Error loading agencies:", error);
      setLoadError("Could not load agencies. Try adjusting your filters.");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, filters]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  useEffect(() => {
    if (filtersResetKey) {
      setCurrentPage(1);
    }
  }, [filtersResetKey]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const clearFilters = () => {
    setSearchQuery("");
    setStateFilter("all");
  };

  const hasActiveFilters = searchQuery || stateFilter !== "all";

  const toggleSort = (field: SortField) => {
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder(field === "agency_name" ? "asc" : "desc");
      return;
    }
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const coveragePercent =
    stats.totalProperties > 0
      ? Math.round((stats.propertiesWithAgency / stats.totalProperties) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Agencies</h1>
          <p className="text-muted-foreground">
            Real estate agencies derived from scraped property data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="py-1.5">
            <Building2 className="w-3.5 h-3.5 mr-1" />
            {stats.uniqueAgencies.toLocaleString()} agencies
          </Badge>
          <Button
            variant="outline"
            size="icon"
            onClick={loadAgencies}
            disabled={isLoading}
            type="button"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Unique Agencies</p>
            <p className="text-2xl font-bold mt-1">
              {stats.uniqueAgencies.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Properties with Agency</p>
            <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">
              {stats.propertiesWithAgency.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Properties</p>
            <p className="text-2xl font-bold mt-1">
              {stats.totalProperties.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Coverage</p>
            <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
              {coveragePercent}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="agency-search"
                name="agencySearch"
                placeholder="Search agency name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1"
                type="button"
              >
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loadError && <p className="text-sm text-destructive">{loadError}</p>}

      {/* Agency Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    type="button"
                    onClick={() => toggleSort("agency_name")}
                  >
                    Agency Name
                    <ArrowUpDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </TableHead>
                <TableHead className="w-[120px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    type="button"
                    onClick={() => toggleSort("property_count")}
                  >
                    Properties
                    <ArrowUpDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </TableHead>
                <TableHead className="w-[180px]">States</TableHead>
                <TableHead className="w-[200px]">Listing Types</TableHead>
                <TableHead className="w-[140px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    type="button"
                    onClick={() => toggleSort("last_seen_at")}
                  >
                    Last Seen
                    <ArrowUpDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-2">
                      Loading agencies...
                    </p>
                  </TableCell>
                </TableRow>
              ) : agencies.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-12 text-muted-foreground"
                  >
                    <div className="space-y-2">
                      <Building2 className="w-8 h-8 mx-auto opacity-50" />
                      <p>No agencies found</p>
                      <p className="text-sm">
                        {hasActiveFilters
                          ? "Try adjusting your filters"
                          : "Run a scrape job to populate agency data"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                agencies.map((agency) => (
                  <TableRow
                    key={agency.agency_name}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(
                        `/dashboard/properties?search=${encodeURIComponent(agency.agency_name)}`,
                      )
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium text-sm">
                          {agency.agency_name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Home className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {agency.property_count.toLocaleString()}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {agency.states.map((state) => (
                          <Badge
                            key={state}
                            variant="outline"
                            className="text-xs px-1.5 py-0"
                          >
                            <MapPin className="w-3 h-3 mr-0.5" />
                            {state}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {agency.listing_types.map((lt) => (
                          <Badge
                            key={lt}
                            variant="secondary"
                            className="text-xs px-1.5 py-0"
                          >
                            {LISTING_TYPE_LABELS[lt] ?? lt}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatTimeAgo(agency.last_seen_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(currentPage * PAGE_SIZE, totalCount)} of{" "}
                {totalCount.toLocaleString()} agencies
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages || isLoading}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Agencies;
